import React, { FC, useMemo } from 'react';

import { Flex, Card, Button } from '@opendoor/bricks/core';
import { colors } from '@opendoor/bricks/theme/eero';
import { DocumentCard } from '@opendoor/cinderblocks/sell/DocumentCard';

import { useHistory } from 'platform/HistoryProvider';
import {
  HelloSignButtons,
  HelloSignButtonsProps,
} from 'sellDirect/components/closingDash/HelloSignButtons';
import { AgentDocumentUpload } from 'sellDirect/components/closingDash/document/AgentDocumentUpload';
import { FinalizedOffer, UnderContractOffer, ClosedOffer } from 'sellDirect/OfferContext';
import { getHubSellingRoute } from 'shared/helpers/routes';
import { ContentTable, IContentTableRow } from 'sellDirect/components/closingDash/ContentTable';
import { NO, YES } from 'sellDirect/components/closingDash/form/FormRadioInput';
import { ClosingTasks, ImprovedHelloSignTaskFields } from 'sellDirect/helpers/closingTasks';
import { useHelloSignDownload } from 'sellDirect/helpers/closingDash/helloSign';
import { capitalizedSnakeCaseToWords } from 'shared/helpers/formatters';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

export type WizardSummaryProps =
  | (BaseWizardSummaryProps & WizardHelloSignButtonsProps)
  | BaseWizardSummaryProps;

/**
 * Renders the styled summary for a form in the wizard
 *
 * component-test-check-ignore
 */
export const WizardSummary: FC<WizardSummaryProps> = (props) => {
  const { rows, trackingName, onEdit, disableEdit } = props;
  return (
    <Flex gridGap="8" flexDir="column" data-testid={trackingName}>
      <BaseWizardSummary
        rows={rows}
        trackingName={trackingName}
        onEdit={onEdit}
        disableEdit={disableEdit}
      />
      {'offer' in props && (
        <WizardHelloSignButtons
          offer={props.offer}
          helloSignTask={props.helloSignTask}
          onClickSignButton={props.onClickSignButton}
          showInvite={props.showInvite}
        />
      )}
    </Flex>
  );
};

export interface BaseWizardSummaryProps {
  /** The data to display */
  rows: IContentTableRow[];
  /** The name to use for tracking */
  trackingName: string;
  /** Callback when the user clicks the edit button */
  onEdit?: () => void;
  /** Boolean to disable the edit button */
  disableEdit: boolean;
}

/**
 * Renders the styled content table for a form in the wizard
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const BaseWizardSummary: FC<BaseWizardSummaryProps> = ({
  rows,
  trackingName,
  onEdit,
  disableEdit,
}) => {
  const rowStyle = (index: number) => ({
    mt: index === 0 ? 0 : 4,
    pb: 4,
    borderBottom: `1px solid ${colors.neutrals30}`,
  });

  return (
    <Card variant="tertiary" p={[7, 7, 7]} pb={[6, 6, 6]}>
      <ContentTable
        rows={rows}
        trackingName={trackingName}
        rowStyle={rowStyle}
        valueStyle={{ fontWeight: 'semibold' }}
        titleStyle={{
          fontSize: ['s0', null, 's1'],
          lineHeight: ['s0', null, 's1'],
        }}
      />
      {onEdit && (
        <Button
          mt="5"
          size="sm"
          analyticsName={`cake-edit-${trackingName}`}
          data-testid={`edit-${trackingName}`}
          disabled={disableEdit}
          variant="secondary"
          onClick={onEdit}
          width="fit-content"
          alignSelf="flex-end"
          aria-label={`Edit ${trackingName}`}
        >
          Edit
        </Button>
      )}
    </Card>
  );
};
export interface WizardHelloSignButtonsProps {
  /** The current offer offer progress from DB */
  offer: FinalizedOffer | UnderContractOffer | ClosedOffer;
  helloSignTask?: ImprovedHelloSignTaskFields;
  /** Callback when user clicks Sign for a document and signatory pair */
  onClickSignButton: HelloSignButtonsProps['onClickSignButton'];
  /** Whether the user can send invitations. Defaults to false. */
  showInvite?: boolean;
}

/**
 * Renders the buttons to complete the document.
 * If the customer is an agent, it would show the "Upload document" button
 * If the customer is core, it would show the "Sign as X" buttons for each signer
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const WizardHelloSignButtons: FC<WizardHelloSignButtonsProps> = ({
  offer,
  helloSignTask,
  onClickSignButton,
  showInvite = false,
}) => {
  const { history, goBack } = useHistory();
  const initialLocation = useMemo(() => history.location, [history]);
  const helloSignDownload = useHelloSignDownload();
  const { addressUUID } = useQualificationContext();

  return (
    <>
      {!!helloSignTask && !helloSignTask.completedAt && (
        <DocumentCard
          type="custom-link"
          name={capitalizedSnakeCaseToWords(helloSignTask.documentType || '')}
          id={`hello-sign-download-card-${helloSignTask.documentType}`}
          customLink={
            <Button
              analyticsName={`cake-document-tile-download-${capitalizedSnakeCaseToWords(
                helloSignTask.documentType || '',
              )}`}
              onClick={() => helloSignDownload(helloSignTask.id)}
              variant="link"
              aria-label={null}
              fontWeight="semibold"
            >
              Download
            </Button>
          }
        />
      )}
      {helloSignTask?.signers && onClickSignButton && !offer.shouldSeeAgentExperience && (
        <HelloSignButtons
          signers={helloSignTask.signers}
          onClickSignButton={onClickSignButton}
          showInvite={showInvite}
        />
      )}
      {offer.shouldSeeAgentExperience && helloSignTask && !helloSignTask.completedAt && offer && (
        <AgentDocumentUpload
          offer={offer}
          task={helloSignTask}
          onUploaded={() =>
            goBack({
              before: initialLocation,
              fallbackLocation: getHubSellingRoute(),
              addressUUID,
            })
          }
        />
      )}
    </>
  );
};

/**
 * Given ClosingTasks, returns the list of signatories formatted for IContentTableRow.
 *
 * Note we do not use HelloSignTask.taskable.signers because that returns which names/emails are
 * have access to the HelloSign document. This doesn't work for agent leads because we only give
 * the agent access to the HelloSign document. Therefore, we use the signatories that were entered
 * during the SetSignatoriesTask.
 */
export const getSignersRows = (closingTasks: ClosingTasks): IContentTableRow[] => {
  return (
    closingTasks.setSignatories?.taskable.signerNames.map((signer, index) => ({
      title: index === 0 ? 'Owner’s name' : 'Second owner’s name',
      trackingId: index === 0 ? 'owner' : 'second-owner',
      value: signer,
    })) ?? []
  );
};

/**
 * format task detail into right format to display in summary table.
 */
export const getTaskDetailRow = (
  title: string,
  value: string | boolean | null | undefined,
): IContentTableRow => {
  return {
    title,
    value: typeof value === 'boolean' ? (value ? YES : NO) : value ?? 'N/A',
    trackingId: title.toLowerCase().split(' ').join('-'),
  };
};
